import { useMemo, useState } from 'react';
import {
  Box, Button, Card, Chip, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import type { GridColDef } from '@mui/x-data-grid';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter } from '../../components/data-grid/RouteDataGrid';
import type { ExportColumn } from '../../utils/export';
import { fmtDateTime } from '../../utils/format';
import { apiBaseUrl, TOKEN_STORAGE_KEY } from '../../auth/config';
import { apiFetch } from '../../api/client';
import { useAccessToken } from '../../auth/useAccessToken';
import type { IotQrPairSummary } from '../../api/types';
import {
  useApplyIotQrBatchHeader, useClearIotQrBatchHeader,
  useIotQrBatchDetail,
  useStickerHeaderTemplates,
} from '../../api/queries';
import { useAlert } from '../../components/dialogs/DialogProvider';

/**
 * Detail view for a single IoT (HAT) QR batch — mirror of
 * ScQrBatchDetailPage. Header card with batch metadata + lifecycle
 * status, grid of every QR pair in the batch, "Open print preview"
 * button that navigates to the print page where the PDF can be
 * previewed / downloaded / printed.
 *
 * Header-template snapshotting is omitted here — the IoT factory API
 * doesn't expose header endpoints yet. When that lands, copy the SC
 * detail page's header card across.
 */
export default function IotQrBatchDetailPage() {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const alert = useAlert();

  const batchQ = useIotQrBatchDetail(batchId ?? null);
  const getToken = useAccessToken();
  const headersQ = useStickerHeaderTemplates();
  const applyHeader = useApplyIotQrBatchHeader();
  const clearHeader = useClearIotQrBatchHeader();
  const [headerError, setHeaderError] = useState<string | null>(null);

  const batch = batchQ.data;
  usePageTitle(batch ? `Batch ${batch.code}` : 'IoT QR batch');
  // Header is a print-time snapshot — editable on any non-voided batch.
  const canEditHeader = !!batch && batch.status !== 'voided';

  const batchChipColour = (s: string) =>
    s === 'created' ? 'info'    :
    s === 'printed' ? 'success' :
    s === 'voided'  ? 'error'   :
    'default';

  const pairChipColour = (s: string) =>
    s === 'printed' ? 'default' :
    s === 'in_use'  ? 'success' :
    s === 'retired' ? 'warning' :
    s === 'voided'  ? 'error'   :
    'default';

  const columns = useMemo<GridColDef<IotQrPairSummary>[]>(() => [
    { field: 'qrId', headerName: 'QR id', flex: 1.2,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{p.row.qrId}</Typography>
        </Box>
      ) },
    { field: 'status', headerName: 'Status', width: 120,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip size="small" label={p.row.status} color={pairChipColour(p.row.status) as 'success' | 'warning' | 'error' | 'default'} />
        </Box>
      ) },
    { field: 'machineAssetNumber', headerName: 'Bound to machine (asset #)', flex: 1.4,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 11 }}>
            {p.row.machineAssetNumber ?? '—'}
          </Typography>
        </Box>
      ) },
    { field: 'createdAt', headerName: 'Created', width: 170,
      renderCell: (p) => <Typography variant="caption">{fmtDateTime(p.row.createdAt)}</Typography> },
    { field: 'voidedAt', headerName: 'Voided', width: 170,
      renderCell: (p) => <Typography variant="caption">{p.row.voidedAt ? fmtDateTime(p.row.voidedAt) : '—'}</Typography> },
    { field: 'retiredAt', headerName: 'Retired', width: 170,
      renderCell: (p) => <Typography variant="caption">{p.row.retiredAt ? fmtDateTime(p.row.retiredAt) : '—'}</Typography> },
  ], []);

  const quickFilters = useMemo<QuickFilter<IotQrPairSummary>[]>(() => [
    { label: 'All',     filter: () => true },
    { label: 'Printed', filter: (p) => p.status === 'printed' },
    { label: 'In use',  filter: (p) => p.status === 'in_use' },
    { label: 'Retired', filter: (p) => p.status === 'retired' },
    { label: 'Voided',  filter: (p) => p.status === 'voided' },
  ], []);

  const exportColumns = useMemo<ExportColumn<IotQrPairSummary>[]>(() => [
    { key: 'qrId',               label: 'QR id' },
    { key: 'payload',            label: 'Payload' },
    { key: 'status',             label: 'Status' },
    { key: 'machineAssetNumber', label: 'Machine asset #' },
    { key: 'machineId',          label: 'Machine id (internal)' },
    { key: 'createdAt',          label: 'Created',  format: (v) => fmtDateTime(v) },
    { key: 'voidedAt',           label: 'Voided',   format: (v) => fmtDateTime(v) },
    { key: 'retiredAt',          label: 'Retired',  format: (v) => fmtDateTime(v) },
  ], []);

  return (
    <Stack spacing={2}>
      <Button onClick={() => navigate('/factory/iot-qr-batches')} sx={{ alignSelf: 'flex-start' }}>
        ← Back to batches
      </Button>

      {batchQ.isLoading && <Typography>Loading…</Typography>}
      {batchQ.error && <ErrorAlert>Failed to load batch.</ErrorAlert>}

      {batch && (
        <>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={4} flexWrap="wrap" alignItems="center">
              <Stack>
                <Typography variant="overline" color="text.secondary">Batch code</Typography>
                <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>{batch.code}</Typography>
              </Stack>
              <Stack>
                <Typography variant="overline" color="text.secondary">Pairs</Typography>
                <Typography variant="h6">{batch.generatedCount.toLocaleString()}</Typography>
              </Stack>
              <Stack>
                <Typography variant="overline" color="text.secondary">Bound</Typography>
                <Typography variant="h6">{batch.boundCount.toLocaleString()} / {batch.generatedCount.toLocaleString()}</Typography>
              </Stack>
              <Stack>
                <Typography variant="overline" color="text.secondary">Status</Typography>
                <Chip size="small" label={batch.status} color={batchChipColour(batch.status) as 'success' | 'info' | 'error' | 'default'} />
              </Stack>
              <Stack>
                <Typography variant="overline" color="text.secondary">Created</Typography>
                <Typography variant="body2">{fmtDateTime(batch.createdAt)}</Typography>
              </Stack>
              {batch.printedAt && (
                <Stack>
                  <Typography variant="overline" color="text.secondary">First printed</Typography>
                  <Typography variant="body2">{fmtDateTime(batch.printedAt)}</Typography>
                </Stack>
              )}
              {batch.voidedAt && (
                <Stack>
                  <Typography variant="overline" color="text.secondary">Voided</Typography>
                  <Typography variant="body2">{fmtDateTime(batch.voidedAt)}</Typography>
                </Stack>
              )}
              <Box sx={{ flex: 1 }} />
            </Stack>
            {batch.notes && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{batch.notes}</Typography>
            )}
          </Card>

          <Card variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Stack sx={{ flex: 1 }} spacing={0.5}>
                <Typography variant="overline" color="text.secondary">Header (optional)</Typography>
                <Typography variant="body2" color="text.secondary">
                  Apply a header template (text + optional logo) above
                  the QR on every label in this batch. The design is
                  snapshot-copied at apply time — later edits to the
                  template don't change already-printed batches.
                  {batch.status === 'voided' && (
                    <> Locked: this batch has been voided.</>
                  )}
                </Typography>
                {headerError && <ErrorAlert>{headerError}</ErrorAlert>}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 380 }}>
                <TextField select size="small" sx={{ flex: 1 }}
                  label="QR Code header template"
                  value={batch.headerTemplateId ?? ''}
                  disabled={!canEditHeader || applyHeader.isPending || clearHeader.isPending}
                  onChange={async (e) => {
                    setHeaderError(null);
                    const v = e.target.value;
                    try {
                      if (!v) {
                        if (batch.headerTemplateId) await clearHeader.mutateAsync(batch.id);
                      } else {
                        await applyHeader.mutateAsync({ batchId: batch.id, templateId: v });
                      }
                    } catch (err) {
                      setHeaderError((err as Error).message);
                    }
                  }}>
                  <MenuItem value=""><em>— No header —</em></MenuItem>
                  {(headersQ.data ?? []).map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </TextField>
                <Button size="small" variant="text"
                  onClick={() => navigate('/stickers/header-templates')}>
                  Manage…
                </Button>
              </Stack>
            </Stack>
          </Card>

          <Card variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Stack sx={{ flex: 1 }}>
                <Typography variant="overline" color="text.secondary">Print</Typography>
                <Typography variant="body2" color="text.secondary">
                  Open the print preview to adjust paper / label / grid /
                  margins, print directly, or download the PDF for off-site
                  printing. Each pair prints as <strong>two</strong>
                  {' '}labels (DEVICE + PACKAGING) so a {batch.generatedCount}-pair
                  batch produces {(batch.generatedCount * 2).toLocaleString()} labels.
                </Typography>
              </Stack>
              <Button
                variant="outlined" startIcon={<DownloadIcon />}
                onClick={() => downloadCsv(batch.id, batch.code, alert)}
              >Download CSV</Button>
              <Button
                variant="contained" startIcon={<PrintIcon />}
                onClick={() => navigate(`/factory/iot-qr-batches/${batchId}/print`)}
              >Open print preview</Button>
            </Stack>
          </Card>

          <RouteDataGrid<IotQrPairSummary>
            entityName="IoT QR pairs"
            queryKey={['iot-qr-batches', 'pairs', batchId] as readonly unknown[]}
            listFn={() => apiFetch<IotQrPairSummary[]>(
              `/api/v1/factory/iot-qr-batches/${batchId}/qr-pairs`, {}, getToken)}
            columns={columns}
            searchFields={[{ kind: 'text', key: 'qrId', label: 'QR id' }]}
            quickFilters={quickFilters}
            exportColumns={exportColumns}
            reportTitle={`ARMS — ${batch.code} IoT QR pairs`}
            emptyMessage="No QR pairs in this batch."
          />
        </>
      )}
    </Stack>
  );
}

async function downloadCsv(
  batchId: string, batchCode: string,
  alert: (opts: { title: string; message: string; severity?: 'info' | 'warning' | 'error' | 'success' }) => Promise<void>,
) {
  const url = `${apiBaseUrl}/api/v1/factory/iot-qr-batches/${batchId}/export.csv`;
  const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) {
    await alert({ title: 'Not signed in', message: 'Sign in and retry the download.', severity: 'warning' });
    return;
  }
  try {
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) {
      const text = await r.text();
      throw new Error(`HTTP ${r.status}: ${text || r.statusText}`);
    }
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `iot-qr-batch-${batchCode}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    await alert({ title: 'Download failed', message: (e as Error).message, severity: 'error' });
  }
}
